import { PortableText } from '@portabletext/react'
import { TAB_TYPE, Tab } from '@repo/design-system/index'
import {
  QIcon_calendar_filled,
  QIcon_chat,
  QIcon_chevron_down,
  QIcon_chevron_right,
  QIcon_chevron_up,
  QIcon_clock,
  QIcon_exclamation_outline,
  QIcon_outlink,
} from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { addHours, format } from 'date-fns'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useCallback, useEffect, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import apis from '../../../modules/apis'
import { axiosGet } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { n_content, s_landing_promo } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { RWM_FAQ_IMAGE_1, RWM_FAQ_IMAGE_1_EN } from '../../../modules/images'
import client from '../../../modules/sanityClient'
import { buildSanityImage, ordinal } from '../../../modules/utils'
import { COLORS } from '../../../modules/vars'
import { CONTENT_PROGRAM_DETAIL_DATA_QUERY, CONTENT_PROGRAM_ENROLL_DATA_QUERY } from '../../../sanity/groqQueries'
import { CONTENT_PROGRAM_DETAIL_DATA_QUERYResult, CONTENT_PROGRAM_ENROLL_DATA_QUERYResult } from '../../../sanity/types'
import { convertToEmbedUrl, ptComponents, updateMarksToBreak } from '../../sanity/util'
import { checkWebinarStatus } from '../my-ringle/SectionPrevReadTogether'
import ContentDetailPurchaseButton from './ContentDetailPurchaseButton'
import { checkApplicationStatus, getProgramStatus } from './ContentTypeUtils'
import { IDetailData, IInitialData } from './types'

const fetchDetailData = async (slug: string | string[], isReadTogether: string) => {
  const params = {
    slug,
    contentType: isReadTogether ? 'contentReadTogether' : 'contentReadAlone',
  }

  const data: CONTENT_PROGRAM_DETAIL_DATA_QUERYResult = await client.fetch(CONTENT_PROGRAM_DETAIL_DATA_QUERY, params)
  return data
}

const fetchEnrollData = async (slug: string | string[], isReadTogether: string) => {
  const params = {
    slug,
    contentType: isReadTogether ? 'contentReadTogether' : 'contentReadAlone',
  }

  const data: CONTENT_PROGRAM_ENROLL_DATA_QUERYResult = await client.fetch(CONTENT_PROGRAM_ENROLL_DATA_QUERY, params)
  return data
}

type ContentRWMandSSDetailBodyProps = {
  initialData: IInitialData
  type: string
  webviewSlug?: string
}

export const PROGRAM_TYPE = {
  READ_TOGETHER: 'read-together',
  READ_ALONE: 'read-alone',
}

const TAB = {
  INFORM: 0,
  FAQ: 1,
  LINK: 2,
}
type IContentTutorCard = {
  key: number
  name: string
  education: string
  avatar: any
  profession: string
  introduction: string
}
const ContentTutorCard = ({ name, education, avatar, profession, introduction }: IContentTutorCard) => {
  return (
    <div className={cardStyle.layout}>
      <div className={cardStyle.topLayout}>
        <img src={buildSanityImage(avatar).url()} className="h-[64px] w-[64px] object-cover" alt="tutor_image" />
        <div className={cardStyle.informSection}>
          <div className={cardStyle.name}>{name}</div>
          <div>{profession}</div>
          <div>{education}</div>
        </div>
      </div>
      <div className={cardStyle.bottomLayout}>{introduction}</div>
    </div>
  )
}

const cardStyle = {
  layout: `flex w-full flex-col `,
  topLayout: `flex flex-row gap-[20px] rounded-t-[8px] bg-purple-700 p-[16px] text-white`,
  bottomLayout: `border-1 Body2-14Md rounded-b-[8px] border-t-0 bg-white p-[16px]`,
  informSection: `Body2-14Md`,
  name: `Body1-16Bd`,
}

const FAQSection = ({ faq }) => {
  const [open, setOpen] = useState(false)

  return (
    <div className={faqStyle.layout} onClick={() => setOpen((prev) => !prev)}>
      <div className={faqStyle.title}>
        <div className="w-[90%]">{faq.title}</div>
        <div className="w-[20px]">
          {open ? <QIcon_chevron_up width={20} height={20} /> : <QIcon_chevron_down width={20} height={20} />}
        </div>
      </div>
      {open && <div className={faqStyle.desc}>{faq.desc}</div>}
    </div>
  )
}
const faqStyle = {
  layout: `Body1-16Md cursor-pointer text-gray-700`,
  title: `border-b-1 flex flex-row items-center justify-between border-gray-200 p-[12px]`,
  desc: `bg-gray-50 p-[12px] text-gray-700`,
}

interface IEnrollData {
  downloadLink: string
  replayYoutubeLinkList
}

const DetailBodyComponent = ({ isReadTogether, enrolled, webviewSlug }) => {
  const { query } = useRouter()
  const router = useAppRouter()

  const isWebview = useIsWebview()
  const isLanding = router.asPath.includes('/landing')

  const { t, isLocaleKO } = useTranslation()

  const detailSlug = webviewSlug ? webviewSlug : query.slug
  const [activeTabIdx, setActiveTabIdx] = useState(0)

  const { data: afterEnrollData, isFetching: afterEnrollFetching }: { data: IEnrollData; isFetching: boolean } =
    useQuery({
      queryKey: [isReadTogether ? PROGRAM_TYPE.READ_TOGETHER : PROGRAM_TYPE.READ_ALONE, 'enrolled_data', detailSlug],
      queryFn: () => fetchEnrollData(query.slug, isReadTogether),
      enabled: enrolled,
    })

  const {
    data: detailData,
    isLoading,
    isFetching,
  }: { data: IDetailData; isLoading: boolean; isFetching: boolean } = useQuery({
    queryKey: [isReadTogether ? PROGRAM_TYPE.READ_TOGETHER : PROGRAM_TYPE.READ_ALONE, 'detail', detailSlug],
    queryFn: () => fetchDetailData(detailSlug, isReadTogether),
  })

  const SUB_MENU_LIST = isWebinar(router.query.slug)
    ? [
        {
          name: t(n_content.rwm.detail),
          key: TAB.INFORM,
          anchor: '#Information',
        },
        {
          name: t(n_content.rwm.related),
          key: TAB.LINK,
          anchor: '#Link',
        },
      ]
    : [
        {
          name: t(n_content.rwm.detail),
          key: TAB.INFORM,
          anchor: '#Information',
        },
        {
          name: t(n_content.rwm.faq),
          key: TAB.FAQ,
          anchor: '#FAQ',
        },
        {
          name: t(n_content.rwm.related),
          key: TAB.LINK,
          anchor: '#Link',
        },
      ]

  const FAQ_LIST = [
    {
      title: t(n_content.rwm.faq_list1),
      desc: (
        <>
          <img src={isLocaleKO ? RWM_FAQ_IMAGE_1 : RWM_FAQ_IMAGE_1_EN} alt="faq_image" />
        </>
      ),
    },
    {
      title: t(n_content.rwm.faq_list2),
      desc: t(n_content.rwm.faq_desc1),
    },
    {
      title: t(n_content.rwm.faq_list3),
      desc: t(n_content.rwm.faq_desc2),
    },
    {
      title: t(n_content.rwm.faq_list4),
      desc: t(n_content.rwm.faq_desc3),
    },
    {
      title: t(n_content.rwm.faq_list5),
      desc: t(n_content.rwm.faq_desc4),
    },
    {
      title: t(n_content.rwm.faq_list6),
      desc: t(n_content.rwm.faq_desc5),
    },
    {
      title: t(n_content.rwm.faq_list7),
      desc: t(n_content.rwm.faq_desc6),
    },
  ]

  return (
    <div className="px-[0px] sm:px-[16px] lg:px-[0px]">
      <div
        className={classNames(
          isWebview ? 'top-[-0px]' : isLanding ? 'top-[0px]' : 'top-[60px]',
          'sticky h-[52px] bg-white'
        )}
      >
        <Tab
          className="mx-[16px] h-[52px] bg-white lg:mx-[0px]"
          type={TAB_TYPE.SCROLL}
          activeTabIdx={activeTabIdx}
          setActiveTabIdx={setActiveTabIdx}
        >
          {SUB_MENU_LIST.map((subMenuItem, i) => {
            return (
              <Tab.TabItem
                key={i}
                name={
                  <a
                    href={subMenuItem.anchor}
                    className="Body1-16Bd"
                    onClick={() =>
                      subMenuItem.key == TAB.FAQ
                        ? isWebview
                          ? router.linking(isLocaleKO ? 'https://bit.ly/3NeWOz0' : 'https://bit.ly/48hGYvL')
                          : window.open(isLocaleKO ? 'https://bit.ly/3NeWOz0' : 'https://bit.ly/48hGYvL', '_blank')
                        : null
                    }
                  >
                    {subMenuItem.name}
                  </a>
                }
              ></Tab.TabItem>
            )
          })}
        </Tab>
      </div>
      {afterEnrollData && !afterEnrollFetching && enrolled && afterEnrollData.downloadLink && (
        <div className="Body1-16Bd mx-[16px] mt-[24px] flex items-center justify-center rounded-[7px] bg-purple-50 py-[16px] text-purple-500">
          <div
            onClick={() =>
              isWebview
                ? router.linking(afterEnrollData.downloadLink)
                : window.open(afterEnrollData.downloadLink, '_blank')
            }
            className="cursor-pointer"
          >
            <div className="flex flex-row items-center gap-[4px]">
              <div className="pt-[2px]">{t(n_content.rwm.materials)}</div>
              <QIcon_outlink width={18} height={18} />
            </div>
          </div>
        </div>
      )}
      {afterEnrollData && !afterEnrollFetching && enrolled && afterEnrollData.replayYoutubeLinkList && (
        <div className="mt-[12px] px-[16px]">
          <div className="Title1-20Bd mb-[12px] text-gray-600">{t(n_content.rwm.recorded)}</div>
          <div className="flex flex-col gap-y-[12px]">
            {afterEnrollData.replayYoutubeLinkList?.map((replayItem, i) => {
              return (
                <div key={i} className="">
                  <div className="Body2-14Md mb-[4px] text-gray-700">{replayItem.title}</div>
                  <div className="aspect-[16/9]">
                    <iframe
                      width="100%"
                      height="100%"
                      className="aspect-[16/9]"
                      src={convertToEmbedUrl(replayItem.youtubeLink)}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {isLoading || isFetching ? (
        <></>
      ) : (
        <div className={style.detailBodyLayout}>
          <div className={style.informationTitle}>{t(n_content.rwm.detail)}</div>
          <div className="lists-class keep-all border-b-1 pt-[12px] pb-[40px] ">
            <PortableText value={updateMarksToBreak(detailData.information.content)} components={ptComponents} />
          </div>
          {detailData.tutorList && detailData.tutorList.length > 0 && (
            <>
              <div id="FAQ" className={style.tutorTitle}>
                {t(n_content.rwm.speaker)}
              </div>
              <div className={style.tutorSection}>
                {detailData.tutorList.map((tutor, i) => {
                  return (
                    <ContentTutorCard
                      key={i}
                      avatar={tutor.avatar}
                      name={tutor.name}
                      profession={isLocaleKO ? tutor.profession.profession_ko : tutor.profession.profession_en}
                      education={isLocaleKO ? tutor.education?.education_ko : tutor.education?.education_en}
                      introduction={
                        isLocaleKO ? tutor.introduction.introduction_ko : tutor.introduction.introduction_en
                      }
                    />
                  )
                })}
              </div>
              <div className="border-t-1 mx-auto mt-[40px] h-[8px] w-full border-gray-100 bg-gray-50 " />
            </>
          )}
          {!isWebinar(router.query.slug) && (
            <>
              <div className={style.tutorTitle}>{t(n_content.rwm.faq)}</div>
              <div>
                {FAQ_LIST.map((faq, i) => {
                  return <FAQSection key={i} faq={faq} />
                })}
              </div>
              <div
                className={style.noFAQ}
                onClick={() =>
                  isWebview
                    ? router.linking(isLocaleKO ? 'https://bit.ly/3NeWOz0' : 'https://bit.ly/48hGYvL')
                    : window.open(isLocaleKO ? 'https://bit.ly/3NeWOz0' : 'https://bit.ly/48hGYvL', '_blank')
                }
              >
                <QIcon_outlink width={16} height={16} color={COLORS.GRAY500} />
                <div>{t(n_content.rwm.faq_more)}</div>
              </div>
            </>
          )}

          {detailData.relatedLinkList && detailData.relatedLinkList.length > 0 && (
            <>
              <div className="border-t-1 mx-auto mt-[40px] h-[8px] w-full border-gray-100 bg-gray-50" />
              <p id="Link" className={style.tutorTitle}>
                {t(n_content.rwm.related)}
              </p>
              <div className={style.relatedLinkLayout}>
                {detailData.relatedLinkList.map((listItem, i) => {
                  return (
                    <div
                      onClick={() =>
                        isWebview
                          ? router.push('', {
                              appScreenName: APP_SCREEN_NAME.OUTLINK_SCREEN,
                              appSendData: { url: listItem.link },
                            })
                          : window.open(listItem.link, '_blank')
                      }
                      key={i}
                      className="cursor-pointer"
                    >
                      <div className={style.relatedLinkElement}>
                        <div>{listItem.title}</div>
                        <QIcon_chevron_right width={16} height={16} color={COLORS.GRAY500} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
const LeftSideBody = ({ initialData, isReadTogether, refetch, enrolled, webviewSlug }) => {
  const { t } = useTranslation()

  return (
    <div className={style.leftSideBodyLayout} id="Information">
      <div className={style.imageLayout}>
        <div className={style.imageLayout}>
          <Image
            fill
            alt={initialData.backgroundImage.alt || ' '}
            loading="lazy"
            className=" mx-auto object-cover"
            src={buildSanityImage(initialData.backgroundImage).url()}
            placeholder="blur"
            blurDataURL={buildSanityImage(initialData.backgroundImage).url()}
            quality={100}
          />
        </div>
        <div className={style.heroSection}>
          <div className={style.tagLayout}>
            {getProgramStatus({
              t,
              isEnrolled: enrolled,
              isAvailable: checkApplicationStatus(initialData?.startDate),
              isFinished: isReadTogether
                ? checkWebinarStatus(initialData?.liveWebinarDateList?.liveWebinarDate)
                : new Date() > new Date(initialData?.endDate),
              isBeforeDueDate: new Date() < new Date(initialData?.dueDate),
            })}
            {initialData.tags.map((tag, i) => {
              return (
                <div key={i} className={style.tag}>
                  {tag}
                </div>
              )
            })}
          </div>
          <div className={style.title} style={{ whiteSpace: 'pre-line' }}>
            {initialData.title}
          </div>
          <div className={style.subTitle} style={{ whiteSpace: 'pre-line' }}>
            {initialData.subTitle}
          </div>
        </div>
      </div>
      <div className="flex w-full lg:hidden">
        <RightSideBody
          initialCheckoutOpen={false}
          initialData={initialData}
          isReadTogether={isReadTogether}
          refetch={refetch}
          enrolled={enrolled}
        />
      </div>
      <DetailBodyComponent isReadTogether={isReadTogether} enrolled={enrolled} webviewSlug={webviewSlug} />
    </div>
  )
}

export const isWebinar = (slug) => slug.includes('webinar-event')

const RightSideBody = ({ initialData, isReadTogether, refetch, enrolled, initialCheckoutOpen = true }) => {
  const router = useAppRouter()
  const { t, isLocaleKO, locale } = useTranslation()

  const [open, setOpen] = useState(true)
  const [checkoutOpen, setCheckoutOpen] = useState(initialCheckoutOpen)
  const broswerTime = new Date(initialData.dueDate)

  const liveList = initialData?.liveWebinarDateList?.liveWebinarDate || []

  const CHECKOUT_LIST = isWebinar(router.query.slug)
    ? [
        t(n_content.webinar.note_desc1),
        t(n_content.webinar.note_desc2),
        t(n_content.webinar.note_desc3),
        t(n_content.webinar.note_desc4),
        t(n_content.webinar.note_desc5),
        t(n_content.webinar.note_desc6),
        t(n_content.webinar.note_desc7),
      ]
    : [
        t(n_content.rwm.note_desc1),
        t(n_content.rwm.note_desc2),
        t(n_content.rwm.note_desc3),
        t(n_content.rwm.note_desc4),
        t(n_content.rwm.note_desc5),
        t(n_content.rwm.note_desc6),
      ]

  function convertDate(inputDate) {
    const date = new Date(inputDate)

    const formattedTime = format(addHours(date, 0), 'h:mm a')

    const formattedDate = format(date, 'yyyy.M.d') + ` ${formattedTime}`

    return formattedDate
  }

  function formatDate(date) {
    return format(date, 'yyyy.M.d h:mm a')
  }

  const formatNumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  const formatDateString = (dateString: string) => {
    const parts = dateString.split('-')

    const year = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10)
    const day = parseInt(parts[2], 10)

    return `${year}.${month}.${day}`
  }
  return (
    <div className={style.rightSideLayout}>
      <div className={style.rightSideBodyLayout}>
        <div className={style.priceSection}>
          <div className={style.price}>{formatNumberWithCommas(initialData.price)}</div>
          <div>{t(s_landing_promo.won)}</div>
        </div>
        <div className={style.descSection}>
          <div className={style.descInnerLayout}>
            <div className={style.descElementLayout}>
              <div className={style.descTitle}>
                <QIcon_clock color={COLORS.GRAY500} width={20} height={20} />
                <div>{t(n_content.rwm.info_deadline)}</div>
              </div>
              <div className={style.descValue}>{formatDate(broswerTime)}</div>
            </div>

            {isWebinar(router.query.slug) ? (
              <div className={style.descElementLayout}>
                <div className={style.descTitle}>
                  <QIcon_calendar_filled color={COLORS.GRAY500} width={20} height={20} />
                  <div>{isLocaleKO ? '진행 일시' : 'Date'}</div>
                </div>
                <div className={style.descValue}>
                  {liveList.map((item, i) => {
                    return <div key={i}>{convertDate(item)}</div>
                  })}
                </div>
              </div>
            ) : (
              <div className={style.descElementLayout}>
                <div className={style.descTitle}>
                  <QIcon_calendar_filled color={COLORS.GRAY500} width={20} height={20} />
                  <div>{t(n_content.rwm.info_period)}</div>
                </div>
                <div className={style.descValue}>
                  {formatDateString(initialData.startDate)} - {formatDateString(initialData.endDate)}
                </div>
              </div>
            )}

            <div className={style.descElementLayout}>
              <div className={style.descTitle}>
                <QIcon_chat color={COLORS.GRAY500} width={20} height={20} />
                <div>{t(n_content.rwm.info_format)}</div>
              </div>
              <div className={style.descValue}>{initialData.method}</div>
            </div>
            {!isWebinar(router.query.slug) && isReadTogether && (
              <div className={style.extendSectionLayout}>
                <div className={style.extendSectionTitle} onClick={() => setOpen((prev) => !prev)}>
                  <div>{t(n_content.rwm.note)}</div>
                  {open ? (
                    <QIcon_chevron_up width={20} height={20} color={COLORS.GRAY500} />
                  ) : (
                    <QIcon_chevron_down width={20} height={20} color={COLORS.GRAY500} />
                  )}
                </div>
                {open && (
                  <div className={style.extendDetail}>
                    {liveList.map((item, i) => {
                      return (
                        <div key={i} className={style.extendDetailLayout}>
                          <div className={style.liveTry}>{isLocaleKO ? i + 1 + '회' : ordinal(i + 1)}</div>
                          <div>{convertDate(item)}</div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={style.buttonLayout}>
            <ContentDetailPurchaseButton
              initialData={initialData}
              isReadTogether={isReadTogether}
              refetch={refetch}
              enrolled={enrolled}
            />
          </div>
          <hr className="mt-[24px] lg:hidden" />
        </div>
      </div>
      <div className={style.checkoutSection}>
        <div className={style.checkoutTitle}>
          <div
            className="flex w-full cursor-pointer flex-row items-center justify-between"
            onClick={() => setCheckoutOpen((prev) => !prev)}
          >
            <div className="flex flex-row items-center gap-[4px]">
              <QIcon_exclamation_outline color={COLORS.GRAY500} width={24} height={24} />
              <div>{t(n_content.rwm.note_title)}</div>
            </div>
            {checkoutOpen ? (
              <QIcon_chevron_up width={20} height={20} color={COLORS.GRAY500} />
            ) : (
              <QIcon_chevron_down width={20} height={20} color={COLORS.GRAY500} />
            )}
          </div>
        </div>
        {checkoutOpen && (
          <div className="mt-[12px]">
            {CHECKOUT_LIST.map((item, i) => {
              return (
                <li key={i} className={classNames(style.liElement, 'li-tag-inside')}>
                  {item}
                </li>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const ContentProgramBody = ({ initialData, type, webviewSlug = '' }: ContentRWMandSSDetailBodyProps) => {
  const isReadTogether = type === PROGRAM_TYPE.READ_TOGETHER
  const { currentUser } = useCurrentUser()
  const isWebview = useIsWebview()
  const { sendPageViewEvent } = useEventTracking()
  const router = useAppRouter()
  const { query } = router

  useEffect(() => {
    if (!router) return
    const detailSlug = webviewSlug ? webviewSlug : query.slug
    sendPageViewEvent({
      page_name: type === PROGRAM_TYPE.READ_TOGETHER ? 'read_together' : 'read_alone',
      object_context: String(detailSlug),
    })
  }, [router.asPath])

  // ================== Checkout Enrolled Status ==================
  const [enrolled, setEnrolled] = useState(false)

  const fetchEnrolledData = useCallback(async () => {
    if (!currentUser) return

    try {
      const response = await axiosGet(apis.contentPrograms.enrolled(), {
        param: { sanity_content_type: initialData._type, sanity_id: initialData._id },
        shouldThrowError: true,
      })

      return response // Make sure to return the data property if it exists
    } catch (e) {
      console.error('Error fetching enrolled data:', e) // Log the error for better debugging
      alert(e.response?.data?.error || e.message) // Handle potential undefined e.response
    }
  }, [currentUser, initialData?._type, initialData?._id]) // Dependencies for useCallback

  const {
    data: enrolledData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['enrolled', currentUser?.id, initialData._id],
    queryFn: () => fetchEnrolledData(),
    enabled: !!currentUser,
  })

  useEffect(() => {
    if (enrolledData && !isFetching && currentUser) {
      setEnrolled(enrolledData.enrolled)
    }
  }, [enrolledData, isFetching, currentUser]) // Dependencies for useEffect

  return (
    <div className={style.layout}>
      {/** 결제 script (IAMPORT) 5/23일 결제 모듈 1.2.0 업데이트 **/}
      <Script type="text/javascript" src="https://code.jquery.com/jquery-1.12.4.min.js" strategy="afterInteractive" />
      <Script
        type="text/javascript"
        src="https://cdn.iamport.kr/js/iamport.payment-1.2.0.js"
        strategy="afterInteractive"
      />
      {/** 결제 script (TOSS) **/}
      <Script type="text/javascript" src="https://js.tosspayments.com/v1" strategy="afterInteractive" />

      <LeftSideBody
        initialData={initialData}
        isReadTogether={isReadTogether}
        refetch={refetch}
        enrolled={enrolled}
        webviewSlug={webviewSlug}
      />
      <div className="relative hidden lg:flex lg:w-[336px] lg:flex-col ">
        <div className="flex lg:w-[336px]">
          <RightSideBody
            initialCheckoutOpen={true}
            initialData={initialData}
            isReadTogether={isReadTogether}
            refetch={refetch}
            enrolled={enrolled}
          />
        </div>
      </div>
      <div
        className={classNames(
          isWebview ? 'sticky bottom-[0px] pb-[32px]' : 'fixed bottom-[0px] pb-[16px]',
          'w-full bg-white px-[16px] pt-[12px] sm:px-[32px] lg:hidden'
        )}
      >
        <ContentDetailPurchaseButton
          initialData={initialData}
          isReadTogether={isReadTogether}
          refetch={refetch}
          enrolled={enrolled}
        />
      </div>
    </div>
  )
}

export default ContentProgramBody

const style = {
  layout: `relative flex flex-col gap-[16px] lg:mt-[40px] lg:flex-row`,
  leftSideBodyLayout: `relative w-full px-0 sm:w-full lg:w-[calc(100%_-_352px)]`,
  rightSideBodyLayout: `lg:border-1 rounded-[8px] lg:border-gray-200 lg:p-[16px]`,
  priceSection: `Title2-18Md border-b-1 flex flex-row items-center gap-[4px] pb-[16px]`,
  price: `Headline1-28Bd`,
  descSection: `mt-[16px] flex flex-col justify-between`,
  descInnerLayout: `flex flex-col justify-between gap-[16px]`,
  buttonLayout: `mt-[25px] hidden w-full lg:mt-[32px] lg:block`,
  descElementLayout: `flex flex-row items-start justify-between gap-[20px]`,
  descTitle: ` Body2-14Md flex min-w-[76px] flex-row items-start gap-[4px] py-[2px] text-gray-500`,
  descValue: `Body1-16Md`,
  extendSectionLayout: ` rounded-[8px] bg-gray-50 py-[12px] px-[16px] `,
  extendSectionTitle: `Body2-14Md flex cursor-pointer flex-row justify-between text-gray-600`,
  extendDetail: `mt-[12px] flex flex-col items-start gap-[4px]`,
  extendDetailLayout: `Body2-14Md flex flex-row text-gray-600`,
  liveTry: `mr-[8px] w-[40px]`,
  checkoutSection: `relative mt-[24px] rounded-[8px] bg-gray-50 p-[16px] lg:mt-[20px]`,
  rightSideLayout: `w-full gap-[20px] px-[16px] pt-[20px] sm:px-[32px] lg:p-[0px]`,
  checkoutTitle: `Title2-18Bd flex flex-row items-center gap-[4px] text-gray-600`,
  liElement: `Body2-14Md text-gray-600`,
  imageLayout: `relative h-[320px] max-h-[200px] w-[100vw] lg:h-[200px] lg:max-h-[200px] lg:w-full`,
  heroSection: `absolute top-0 py-[20px] px-[16px]`,
  tagLayout: `flex flex-row gap-[8px]`,
  tag: `Caption-12Bd flex items-center justify-center rounded-[4px] bg-gray-100 px-[6px] py-[2px] text-gray-600`,
  title: `Title1-20Bd mt-[16px] whitespace-pre-line text-white`,
  subTitle: `Body1-16Md mt-[8px] whitespace-pre-line text-gray-100`,
  detailBodyLayout: `px-[16px] pt-[24px]`,
  informationTitle: `Title1-20Bd text-gray-600`,
  tutorTitle: `Title1-20Bd mt-[24px] mb-[12px] text-gray-600`,
  tutorSection: `flex flex-col gap-[12px]`,
  noFAQ: `Body2-14Md mt-[12px] flex cursor-pointer flex-row items-center justify-end gap-[4px] text-gray-500`,
  relatedLinkLayout: `flex flex-col gap-[12px]`,
  relatedLinkElement: `Body2-14Bd border-1 flex justify-between rounded-[8px] p-[16px]`,
  submitPossibleTag: `Caption-12Bd flex items-center justify-center rounded-[4px] bg-blue-100 px-[6px] py-[2px] text-blue-600`,
  submitImPossibleTag: `Caption-12Bd flex items-center justify-center rounded-[4px] bg-red-100 px-[6px] py-[2px] text-red-600`,
}
